import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, BrainCircuit,
  Globe2,
  Smartphone,
  Workflow,
  Terminal,
  Zap,
} from "lucide-react";

export default function Hero() {
  return (
    <section className="relative w-full lg:w-[80%] mx-auto px-4 sm:px-6 pt-16 pb-10 grid lg:grid-cols-2 gap-12 items-center">
      <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ec4899_1px,transparent_1px),linear-gradient(to_bottom,#7c3aed_1px,transparent_1px)] bg-[size:44px_44px]" />
      <motion.div className="relative z-10" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-pink-100 mb-6">
           AI-Powered Corporate IT Solutions
        </div>
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-tight">
          We Engineer Technology That <span className="bg-gradient-to-r from-pink-500 via-fuchsia-400 to-violet-600 bg-clip-text text-transparent">Moves Businesses Forward</span>
        </h1>
        <p className="mt-6 text-lg text-slate-300 max-w-xl">
          OjMee Technology delivers intelligent digital solutions for modern businesses — from AI-powered websites and mobile applications to ERP, HRMS, CRM, billing, dashboards, automation, and custom enterprise software.
        </p>
        <p className="mt-6 text-lg text-slate-300 max-w-xl">
          We combine AI, modern software engineering, and business automation to build secure, scalable, and high-performance technology that helps organizations work smarter and grow faster.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/services" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-pink-500 to-violet-700 font-bold shadow-xl shadow-pink-500/20">
            Explore Services <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/contact" className="inline-flex items-center justify-center px-7 py-4 rounded-full border border-white/15 bg-white/5 font-bold hover:bg-white/10">
            Start Project
          </Link>
        </div>
      </motion.div>

      <motion.div
        className="relative z-10 w-full max-w-xl"
        initial={{ opacity: 0, scale: 0.88, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >

        <div className="absolute -inset-10 rounded-full bg-pink-600/20 blur-[100px]" />
        <div className="absolute -inset-5 rounded-[3rem] bg-violet-600/10 blur-3xl" />

        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/[0.07] p-6 shadow-[0_30px_100px_rgba(0,0,0,0.45)] backdrop-blur-2xl">

          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-violet-600 shadow-lg shadow-pink-500/30">
                <BrainCircuit className="h-5 w-5 text-white" />
              </div>

              <div>
                <p className="text-sm font-bold text-white">OjMee AI</p>
                <p className="text-[10px] tracking-[0.2em] text-slate-400">
                  INTELLIGENCE ENGINE
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
              <span className="text-[10px] font-bold tracking-wider text-emerald-300">
                SYSTEM ONLINE
              </span>
            </div>
          </div>

          <div className="relative mt-8 flex h-72 items-center justify-center">

            <motion.div
              className="absolute h-64 w-64 rounded-full border border-pink-500/20"
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              className="absolute h-52 w-52 rounded-full border border-violet-500/25 border-dashed"
              animate={{ rotate: -360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            />

            <div className="absolute h-44 w-44 rounded-full bg-pink-500/20 blur-3xl" />

            <motion.div
              className="absolute h-64 w-64"
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            >
              <span className="absolute -top-1 left-1/2 h-3 w-3 rounded-full bg-pink-400 shadow-[0_0_20px_#ec4899]" />
            </motion.div>

            <motion.div
              className="absolute h-52 w-52"
              animate={{ rotate: -360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            >
              <span className="absolute top-1/2 -right-1 h-2.5 w-2.5 rounded-full bg-violet-400 shadow-[0_0_20px_#8b5cf6]" />
            </motion.div>

            <motion.div
              className="relative flex h-36 w-36 items-center justify-center rounded-full border border-white/20 bg-gradient-to-br from-pink-500 via-fuchsia-600 to-violet-700 shadow-[0_0_70px_rgba(236,72,153,0.45)]"
              animate={{
                boxShadow: [
                  "0 0 50px rgba(236,72,153,.35)",
                  "0 0 90px rgba(139,92,246,.55)",
                  "0 0 50px rgba(236,72,153,.35)",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#090816]/90 shadow-inner">
                <BrainCircuit className="h-12 w-12 text-pink-300" />
              </div>
            </motion.div>

            <motion.div
              className="absolute left-2 top-8 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 backdrop-blur-xl"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white">AI Powered</span>
              </div>
              <p className="mt-1 text-[10px] text-slate-400">
                Intelligent automation
              </p>
            </motion.div>

            <motion.div
              className="absolute right-0 bottom-8 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 backdrop-blur-xl"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
            >
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-violet-400" />
                <span className="text-xs font-bold text-white">Smart Systems</span>
              </div>
              <p className="mt-1 text-[10px] text-slate-400">
                Built to scale
              </p>
            </motion.div>
          </div>

          <div className="relative z-10 text-center">
            <h3 className="text-4xl font-black tracking-tight text-white sm:text-5xl">
              OjMee{" "}
              <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                AI
              </span>
            </h3>

            <p className="mt-2 text-xs font-semibold tracking-[0.35em] text-slate-400">
              INTELLIGENT SOFTWARE • AUTOMATION
            </p>
          </div>

          <div className="relative z-10 mt-7 grid grid-cols-3 gap-3">

            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:bg-white/10">
              <Globe2 className="h-5 w-5 text-pink-400" />
              <p className="mt-3 text-xs font-bold text-white">Web</p>
              <p className="mt-1 text-[9px] text-slate-500">AI Websites</p>
            </div>

            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:bg-white/10">
              <Smartphone className="h-5 w-5 text-violet-400" />
              <p className="mt-3 text-xs font-bold text-white">Mobile</p>
              <p className="mt-1 text-[9px] text-slate-500">iOS & Android</p>
            </div>

            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:bg-white/10">
              <Workflow className="h-5 w-5 text-fuchsia-400" />
              <p className="mt-3 text-xs font-bold text-white">Automation</p>
              <p className="mt-1 text-[9px] text-slate-500">Smart Workflows</p>
            </div>

          </div>

          <div className="relative z-10 mt-4 rounded-2xl border border-pink-500/15 bg-black/30 p-4">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-pink-400" />
              <span className="text-[10px] font-bold tracking-wider text-slate-400">
                AI ENGINE
              </span>
            </div>

            <div className="mt-3 flex items-center gap-2 font-mono text-xs">
              <span className="text-pink-400">$</span>
              <span className="text-slate-300">
                automate.business<span className="animate-pulse">_</span>
              </span>
            </div>
          </div>

        </div>
      </motion.div>
    </section>
  );
}
